import React, { useEffect, useState, useRef, Component } from 'react';
import { ArrowLeftIcon, SearchIcon, FilterIcon, HistoryIcon, PackageIcon, ClockIcon, ArrowRightIcon, CheckCircleIcon, InfoIcon, CookingPotIcon, BoxIcon, ScaleIcon, ArrowDownIcon, ChevronDownIcon, ChevronRightIcon, ExternalLinkIcon } from 'lucide-react';
interface TraceabilityModuleProps {
  onBackToOverview?: () => void;
}
// Interfaces for traceability data
interface Material {
  id: string;
  name: string;
  type: string;
  supplier?: string;
  batchNumber?: string;
  amount: number;
  unit: string;
}
interface ProcessStep {
  id: string;
  type: 'weighing' | 'mixing' | 'cooking' | 'packaging' | 'quality' | 'storage';
  name: string;
  timestamp: Date;
  duration?: number; // in minutes
  operator?: string;
  machine?: string;
  temperature?: number;
  inputMaterials: Material[];
  outputMaterials?: Material[];
  batchNumber?: string;
  notes?: string;
}
interface ProductionOrder {
  id: string;
  name: string;
  batchNumber: string;
  startDate: Date;
  endDate?: Date;
  status: 'in-progress' | 'completed' | 'on-hold';
  processSteps: ProcessStep[];
}
export function TraceabilityModule({
  onBackToOverview
}: TraceabilityModuleProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'in-progress' | 'completed' | 'on-hold'>('all');
  const [selectedOrder, setSelectedOrder] = useState<ProductionOrder | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<{
    [key: string]: boolean;
  }>({});
  const [activeTab, setActiveTab] = useState<'timeline' | 'flowchart'>('timeline');
  // Track matching process steps for search highlighting
  const [matchingStepIds, setMatchingStepIds] = useState<string[]>([]);
  // Refs for scrolling to matching steps
  const stepRefs = useRef<{
    [key: string]: HTMLDivElement | null;
  }>({});
  // Mock data for production orders with traceability information
  const [productionOrders, setProductionOrders] = useState<ProductionOrder[]>([{
    id: '1001',
    name: 'Gustoria Paella',
    batchNumber: 'B2023-P-1001',
    startDate: new Date(2023, 9, 15, 8, 30),
    endDate: new Date(2023, 9, 15, 14, 45),
    status: 'completed',
    processSteps: [{
      id: 'PS1001-1',
      type: 'weighing',
      name: 'Rohstoffverwiegung - Reis',
      timestamp: new Date(2023, 9, 15, 8, 30),
      duration: 15,
      operator: 'M. Schmidt',
      machine: 'Waage-01',
      inputMaterials: [{
        id: 'R001',
        name: 'Langkornreis',
        type: 'Grundzutat',
        supplier: 'Reis GmbH',
        batchNumber: 'R-20231010-01',
        amount: 25,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'R001-W',
        name: 'Langkornreis (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'R-20231010-01-W',
        amount: 25,
        unit: 'kg'
      }],
      batchNumber: 'W-1001-1',
      notes: 'Verwiegung ohne Abweichungen'
    }, {
      id: 'PS1001-2',
      type: 'weighing',
      name: 'Rohstoffverwiegung - Gemüse',
      timestamp: new Date(2023, 9, 15, 8, 45),
      duration: 20,
      operator: 'M. Schmidt',
      machine: 'Waage-02',
      inputMaterials: [{
        id: 'G001',
        name: 'Erbsen',
        type: 'Gemüse',
        supplier: 'Gemüse AG',
        batchNumber: 'G-20231012-03',
        amount: 10,
        unit: 'kg'
      }, {
        id: 'G002',
        name: 'Paprika gewürfelt',
        type: 'Gemüse',
        supplier: 'Gemüse AG',
        batchNumber: 'G-20231013-07',
        amount: 8,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'G001-W',
        name: 'Erbsen (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231012-03-W',
        amount: 10,
        unit: 'kg'
      }, {
        id: 'G002-W',
        name: 'Paprika gewürfelt (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231013-07-W',
        amount: 8,
        unit: 'kg'
      }],
      batchNumber: 'W-1001-2',
      notes: 'Verwiegung ohne Abweichungen'
    }, {
      id: 'PS1001-3',
      type: 'weighing',
      name: 'Rohstoffverwiegung - Fleisch',
      timestamp: new Date(2023, 9, 15, 9, 10),
      duration: 15,
      operator: 'M. Schmidt',
      machine: 'Waage-03',
      inputMaterials: [{
        id: 'F001',
        name: 'Hähnchenfleisch gewürfelt',
        type: 'Protein',
        supplier: 'Fleisch GmbH',
        batchNumber: 'F-20231014-02',
        amount: 15,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'F001-W',
        name: 'Hähnchenfleisch gewürfelt (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'F-20231014-02-W',
        amount: 15,
        unit: 'kg'
      }],
      batchNumber: 'W-1001-3',
      notes: 'Verwiegung ohne Abweichungen'
    }, {
      id: 'PS1001-4',
      type: 'weighing',
      name: 'Gewürzverwiegung',
      timestamp: new Date(2023, 9, 15, 9, 30),
      duration: 25,
      operator: 'S. Weber',
      machine: 'Präzisionswaage-01',
      inputMaterials: [{
        id: 'S001',
        name: 'Safran',
        type: 'Gewürz',
        supplier: 'Gewürz GmbH',
        batchNumber: 'S-20231005-01',
        amount: 0.05,
        unit: 'kg'
      }, {
        id: 'S002',
        name: 'Paprikapulver',
        type: 'Gewürz',
        supplier: 'Gewürz GmbH',
        batchNumber: 'S-20231008-04',
        amount: 0.2,
        unit: 'kg'
      }, {
        id: 'S003',
        name: 'Knoblauchgranulat',
        type: 'Gewürz',
        supplier: 'Gewürz GmbH',
        batchNumber: 'S-20231007-02',
        amount: 0.15,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'S-MIX-001',
        name: 'Paella Gewürzmischung',
        type: 'Gewürzmischung',
        batchNumber: 'SM-1001-1',
        amount: 0.4,
        unit: 'kg'
      }],
      batchNumber: 'W-1001-4',
      notes: 'Gewürzmischung gemäß Rezept erstellt. Batch-Dokumentation vollständig.'
    }, {
      id: 'PS1001-5',
      type: 'mixing',
      name: 'Vormischung Gemüse und Gewürze',
      timestamp: new Date(2023, 9, 15, 10, 0),
      duration: 20,
      operator: 'J. Müller',
      machine: 'Mischer-02',
      temperature: 20,
      inputMaterials: [{
        id: 'G001-W',
        name: 'Erbsen (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231012-03-W',
        amount: 10,
        unit: 'kg'
      }, {
        id: 'G002-W',
        name: 'Paprika gewürfelt (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231013-07-W',
        amount: 8,
        unit: 'kg'
      }, {
        id: 'S-MIX-001',
        name: 'Paella Gewürzmischung',
        type: 'Gewürzmischung',
        batchNumber: 'SM-1001-1',
        amount: 0.4,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'VM-001',
        name: 'Gemüse-Gewürz-Vormischung',
        type: 'Vormischung',
        batchNumber: 'VM-1001-1',
        amount: 18.4,
        unit: 'kg'
      }],
      batchNumber: 'M-1001-1',
      notes: 'Mischung gemäß Rezept. Mischzeit: 15 Minuten. Temperatur konstant bei 20°C.'
    }, {
      id: 'PS1001-6',
      type: 'mixing',
      name: 'Hauptmischung aller Komponenten',
      timestamp: new Date(2023, 9, 15, 10, 30),
      duration: 30,
      operator: 'J. Müller',
      machine: 'Hauptmischer-01',
      temperature: 22,
      inputMaterials: [{
        id: 'R001-W',
        name: 'Langkornreis (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'R-20231010-01-W',
        amount: 25,
        unit: 'kg'
      }, {
        id: 'F001-W',
        name: 'Hähnchenfleisch gewürfelt (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'F-20231014-02-W',
        amount: 15,
        unit: 'kg'
      }, {
        id: 'VM-001',
        name: 'Gemüse-Gewürz-Vormischung',
        type: 'Vormischung',
        batchNumber: 'VM-1001-1',
        amount: 18.4,
        unit: 'kg'
      }, {
        id: 'OL001',
        name: 'Olivenöl',
        type: 'Öl',
        supplier: 'Öl GmbH',
        batchNumber: 'OL-20231001-05',
        amount: 2,
        unit: 'l'
      }],
      outputMaterials: [{
        id: 'HM-001',
        name: 'Paella Hauptmischung',
        type: 'Hauptmischung',
        batchNumber: 'HM-1001-1',
        amount: 60.4,
        unit: 'kg'
      }],
      batchNumber: 'M-1001-2',
      notes: 'Vollständige Mischung aller Komponenten. Mischzeit: 25 Minuten. Temperaturkontrolle dokumentiert.'
    }, {
      id: 'PS1001-7',
      type: 'cooking',
      name: 'Kochen der Paella',
      timestamp: new Date(2023, 9, 15, 11, 15),
      duration: 45,
      operator: 'A. Fischer',
      machine: 'Kochkessel-03',
      temperature: 98,
      inputMaterials: [{
        id: 'HM-001',
        name: 'Paella Hauptmischung',
        type: 'Hauptmischung',
        batchNumber: 'HM-1001-1',
        amount: 60.4,
        unit: 'kg'
      }, {
        id: 'W001',
        name: 'Wasser',
        type: 'Flüssigkeit',
        amount: 30,
        unit: 'l'
      }],
      outputMaterials: [{
        id: 'PF-001',
        name: 'Paella fertig gekocht',
        type: 'Fertigprodukt',
        batchNumber: 'PF-1001-1',
        amount: 90,
        unit: 'kg'
      }],
      batchNumber: 'C-1001-1',
      notes: 'Kochvorgang gemäß Rezept. Kerntemperatur erreicht: 98°C. Kochzeit: 40 Minuten.'
    }, {
      id: 'PS1001-8',
      type: 'quality',
      name: 'Qualitätskontrolle',
      timestamp: new Date(2023, 9, 15, 12, 15),
      duration: 15,
      operator: 'D. Meyer',
      inputMaterials: [{
        id: 'PF-001',
        name: 'Paella fertig gekocht',
        type: 'Fertigprodukt',
        batchNumber: 'PF-1001-1',
        amount: 0.5,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'PF-001-Q',
        name: 'Paella fertig gekocht (QS geprüft)',
        type: 'Fertigprodukt QS',
        batchNumber: 'PF-1001-1-Q',
        amount: 89.5,
        unit: 'kg'
      }],
      batchNumber: 'Q-1001-1',
      notes: 'Sensorische Prüfung bestanden. Mikrobiologische Proben entnommen. Freigabe erteilt.'
    }, {
      id: 'PS1001-9',
      type: 'packaging',
      name: 'Verpackung',
      timestamp: new Date(2023, 9, 15, 13, 0),
      duration: 60,
      operator: 'B. Wagner',
      machine: 'Abfüllanlage-02',
      inputMaterials: [{
        id: 'PF-001-Q',
        name: 'Paella fertig gekocht (QS geprüft)',
        type: 'Fertigprodukt QS',
        batchNumber: 'PF-1001-1-Q',
        amount: 89.5,
        unit: 'kg'
      }, {
        id: 'VP001',
        name: 'Verpackungsmaterial 450g',
        type: 'Verpackung',
        supplier: 'Verpackung GmbH',
        batchNumber: 'VP-20231001-01',
        amount: 200,
        unit: 'Stück'
      }],
      outputMaterials: [{
        id: 'PP-001',
        name: 'Gustoria Paella 450g',
        type: 'Endprodukt',
        batchNumber: 'B2023-P-1001',
        amount: 198,
        unit: 'Stück'
      }],
      batchNumber: 'P-1001-1',
      notes: 'Verpackung gemäß Spezifikation. Gewichtskontrolle durchgeführt. Etikettierung korrekt.'
    }, {
      id: 'PS1001-10',
      type: 'storage',
      name: 'Einlagerung TK-Lager',
      timestamp: new Date(2023, 9, 15, 14, 15),
      duration: 30,
      operator: 'K. Schulz',
      temperature: -18,
      inputMaterials: [{
        id: 'PP-001',
        name: 'Gustoria Paella 450g',
        type: 'Endprodukt',
        batchNumber: 'B2023-P-1001',
        amount: 198,
        unit: 'Stück'
      }],
      outputMaterials: [{
        id: 'PP-001-S',
        name: 'Gustoria Paella 450g (eingelagert)',
        type: 'Endprodukt gelagert',
        batchNumber: 'B2023-P-1001',
        amount: 198,
        unit: 'Stück'
      }],
      batchNumber: 'S-1001-1',
      notes: 'Einlagerung im TK-Lager bei -18°C. Lagerposition: TKL-A2-05. Haltbarkeit: 12 Monate.'
    }]
  }, {
    id: '1002',
    name: 'Gustoria Risotto',
    batchNumber: 'B2023-R-1002',
    startDate: new Date(2023, 9, 16, 9, 0),
    status: 'in-progress',
    processSteps: [{
      id: 'PS1002-1',
      type: 'weighing',
      name: 'Rohstoffverwiegung - Reis',
      timestamp: new Date(2023, 9, 16, 9, 0),
      duration: 15,
      operator: 'M. Schmidt',
      machine: 'Waage-01',
      inputMaterials: [{
        id: 'R002',
        name: 'Risottoreis',
        type: 'Grundzutat',
        supplier: 'Reis GmbH',
        batchNumber: 'R-20231015-02',
        amount: 22,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'R002-W',
        name: 'Risottoreis (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'R-20231015-02-W',
        amount: 22,
        unit: 'kg'
      }],
      batchNumber: 'W-1002-1',
      notes: 'Verwiegung ohne Abweichungen'
    }, {
      id: 'PS1002-2',
      type: 'weighing',
      name: 'Rohstoffverwiegung - Gemüse',
      timestamp: new Date(2023, 9, 16, 9, 20),
      duration: 25,
      operator: 'M. Schmidt',
      machine: 'Waage-02',
      inputMaterials: [{
        id: 'G003',
        name: 'Pilze gewürfelt',
        type: 'Gemüse',
        supplier: 'Gemüse AG',
        batchNumber: 'G-20231015-01',
        amount: 12,
        unit: 'kg'
      }, {
        id: 'G004',
        name: 'Zucchini gewürfelt',
        type: 'Gemüse',
        supplier: 'Gemüse AG',
        batchNumber: 'G-20231015-02',
        amount: 8,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'G003-W',
        name: 'Pilze gewürfelt (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231015-01-W',
        amount: 12,
        unit: 'kg'
      }, {
        id: 'G004-W',
        name: 'Zucchini gewürfelt (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231015-02-W',
        amount: 8,
        unit: 'kg'
      }],
      batchNumber: 'W-1002-2',
      notes: 'Verwiegung ohne Abweichungen'
    }, {
      id: 'PS1002-3',
      type: 'weighing',
      name: 'Gewürzverwiegung',
      timestamp: new Date(2023, 9, 16, 9, 50),
      duration: 20,
      operator: 'S. Weber',
      machine: 'Präzisionswaage-01',
      inputMaterials: [{
        id: 'S004',
        name: 'Basilikum getrocknet',
        type: 'Gewürz',
        supplier: 'Gewürz GmbH',
        batchNumber: 'S-20231010-03',
        amount: 0.1,
        unit: 'kg'
      }, {
        id: 'S005',
        name: 'Oregano',
        type: 'Gewürz',
        supplier: 'Gewürz GmbH',
        batchNumber: 'S-20231012-01',
        amount: 0.08,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'S-MIX-002',
        name: 'Risotto Gewürzmischung',
        type: 'Gewürzmischung',
        batchNumber: 'SM-1002-1',
        amount: 0.18,
        unit: 'kg'
      }],
      batchNumber: 'W-1002-3',
      notes: 'Gewürzmischung gemäß Rezept erstellt. Batch-Dokumentation vollständig.'
    }, {
      id: 'PS1002-4',
      type: 'mixing',
      name: 'Vormischung Gemüse und Gewürze',
      timestamp: new Date(2023, 9, 16, 10, 15),
      duration: 20,
      operator: 'J. Müller',
      machine: 'Mischer-02',
      temperature: 20,
      inputMaterials: [{
        id: 'G003-W',
        name: 'Pilze gewürfelt (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231015-01-W',
        amount: 12,
        unit: 'kg'
      }, {
        id: 'G004-W',
        name: 'Zucchini gewürfelt (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231015-02-W',
        amount: 8,
        unit: 'kg'
      }, {
        id: 'S-MIX-002',
        name: 'Risotto Gewürzmischung',
        type: 'Gewürzmischung',
        batchNumber: 'SM-1002-1',
        amount: 0.18,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'VM-002',
        name: 'Gemüse-Gewürz-Vormischung Risotto',
        type: 'Vormischung',
        batchNumber: 'VM-1002-1',
        amount: 20.18,
        unit: 'kg'
      }],
      batchNumber: 'M-1002-1',
      notes: 'Mischung gemäß Rezept. Mischzeit: 15 Minuten. Temperatur konstant bei 20°C.'
    }, {
      id: 'PS1002-5',
      type: 'mixing',
      name: 'Hauptmischung aller Komponenten',
      timestamp: new Date(2023, 9, 16, 10, 45),
      duration: 30,
      operator: 'J. Müller',
      machine: 'Hauptmischer-01',
      temperature: 21,
      inputMaterials: [{
        id: 'R002-W',
        name: 'Risottoreis (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'R-20231015-02-W',
        amount: 22,
        unit: 'kg'
      }, {
        id: 'VM-002',
        name: 'Gemüse-Gewürz-Vormischung Risotto',
        type: 'Vormischung',
        batchNumber: 'VM-1002-1',
        amount: 20.18,
        unit: 'kg'
      }, {
        id: 'OL001',
        name: 'Olivenöl',
        type: 'Öl',
        supplier: 'Öl GmbH',
        batchNumber: 'OL-20231001-05',
        amount: 1.5,
        unit: 'l'
      }, {
        id: 'D001',
        name: 'Parmesan gerieben',
        type: 'Milchprodukt',
        supplier: 'Milch GmbH',
        batchNumber: 'D-20231014-03',
        amount: 3,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'HM-002',
        name: 'Risotto Hauptmischung',
        type: 'Hauptmischung',
        batchNumber: 'HM-1002-1',
        amount: 46.68,
        unit: 'kg'
      }],
      batchNumber: 'M-1002-2',
      notes: 'Vollständige Mischung aller Komponenten. Mischzeit: 25 Minuten. Temperaturkontrolle dokumentiert.'
    }, {
      id: 'PS1002-6',
      type: 'cooking',
      name: 'Kochen des Risottos',
      timestamp: new Date(2023, 9, 16, 11, 30),
      duration: 50,
      operator: 'A. Fischer',
      machine: 'Kochkessel-02',
      temperature: 95,
      inputMaterials: [{
        id: 'HM-002',
        name: 'Risotto Hauptmischung',
        type: 'Hauptmischung',
        batchNumber: 'HM-1002-1',
        amount: 46.68,
        unit: 'kg'
      }, {
        id: 'W001',
        name: 'Wasser',
        type: 'Flüssigkeit',
        amount: 35,
        unit: 'l'
      }],
      outputMaterials: [{
        id: 'RF-001',
        name: 'Risotto fertig gekocht',
        type: 'Fertigprodukt',
        batchNumber: 'RF-1002-1',
        amount: 81.5,
        unit: 'kg'
      }],
      batchNumber: 'C-1002-1',
      notes: 'Kochvorgang gemäß Rezept. Kerntemperatur erreicht: 95°C. Kochzeit: 45 Minuten. Rührwerk aktiv während des Kochens.'
    }]
  }, {
    id: '1003',
    name: 'Gustoria Bolognese',
    batchNumber: 'B2023-B-1003',
    startDate: new Date(2023, 9, 14, 8, 0),
    endDate: new Date(2023, 9, 14, 15, 30),
    status: 'completed',
    processSteps: [{
      id: 'PS1003-1',
      type: 'weighing',
      name: 'Rohstoffverwiegung - Fleisch',
      timestamp: new Date(2023, 9, 14, 8, 0),
      duration: 20,
      operator: 'M. Schmidt',
      machine: 'Waage-03',
      inputMaterials: [{
        id: 'F002',
        name: 'Rinderhackfleisch',
        type: 'Protein',
        supplier: 'Fleisch GmbH',
        batchNumber: 'F-20231013-01',
        amount: 30,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'F002-W',
        name: 'Rinderhackfleisch (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'F-20231013-01-W',
        amount: 30,
        unit: 'kg'
      }],
      batchNumber: 'W-1003-1',
      notes: 'Verwiegung ohne Abweichungen'
    }, {
      id: 'PS1003-2',
      type: 'weighing',
      name: 'Rohstoffverwiegung - Gemüse',
      timestamp: new Date(2023, 9, 14, 8, 25),
      duration: 25,
      operator: 'M. Schmidt',
      machine: 'Waage-02',
      inputMaterials: [{
        id: 'G005',
        name: 'Zwiebeln gewürfelt',
        type: 'Gemüse',
        supplier: 'Gemüse AG',
        batchNumber: 'G-20231012-05',
        amount: 10,
        unit: 'kg'
      }, {
        id: 'G006',
        name: 'Karotten gewürfelt',
        type: 'Gemüse',
        supplier: 'Gemüse AG',
        batchNumber: 'G-20231013-01',
        amount: 8,
        unit: 'kg'
      }, {
        id: 'G007',
        name: 'Sellerie gewürfelt',
        type: 'Gemüse',
        supplier: 'Gemüse AG',
        batchNumber: 'G-20231013-02',
        amount: 5,
        unit: 'kg'
      }, {
        id: 'G008',
        name: 'Tomaten passiert',
        type: 'Gemüse',
        supplier: 'Tomaten GmbH',
        batchNumber: 'G-20231010-03',
        amount: 25,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'G005-W',
        name: 'Zwiebeln gewürfelt (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231012-05-W',
        amount: 10,
        unit: 'kg'
      }, {
        id: 'G006-W',
        name: 'Karotten gewürfelt (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231013-01-W',
        amount: 8,
        unit: 'kg'
      }, {
        id: 'G007-W',
        name: 'Sellerie gewürfelt (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231013-02-W',
        amount: 5,
        unit: 'kg'
      }, {
        id: 'G008-W',
        name: 'Tomaten passiert (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231010-03-W',
        amount: 25,
        unit: 'kg'
      }],
      batchNumber: 'W-1003-2',
      notes: 'Verwiegung ohne Abweichungen'
    }, {
      id: 'PS1003-3',
      type: 'weighing',
      name: 'Gewürzverwiegung',
      timestamp: new Date(2023, 9, 14, 9, 0),
      duration: 20,
      operator: 'S. Weber',
      machine: 'Präzisionswaage-01',
      inputMaterials: [{
        id: 'S006',
        name: 'Oregano',
        type: 'Gewürz',
        supplier: 'Gewürz GmbH',
        batchNumber: 'S-20231012-01',
        amount: 0.15,
        unit: 'kg'
      }, {
        id: 'S007',
        name: 'Basilikum getrocknet',
        type: 'Gewürz',
        supplier: 'Gewürz GmbH',
        batchNumber: 'S-20231010-03',
        amount: 0.12,
        unit: 'kg'
      }, {
        id: 'S008',
        name: 'Knoblauchgranulat',
        type: 'Gewürz',
        supplier: 'Gewürz GmbH',
        batchNumber: 'S-20231007-02',
        amount: 0.1,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'S-MIX-003',
        name: 'Bolognese Gewürzmischung',
        type: 'Gewürzmischung',
        batchNumber: 'SM-1003-1',
        amount: 0.37,
        unit: 'kg'
      }],
      batchNumber: 'W-1003-3',
      notes: 'Gewürzmischung gemäß Rezept erstellt. Batch-Dokumentation vollständig.'
    }, {
      id: 'PS1003-4',
      type: 'mixing',
      name: 'Gemüsevorbereitung',
      timestamp: new Date(2023, 9, 14, 9, 30),
      duration: 15,
      operator: 'J. Müller',
      machine: 'Mischer-01',
      temperature: 20,
      inputMaterials: [{
        id: 'G005-W',
        name: 'Zwiebeln gewürfelt (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231012-05-W',
        amount: 10,
        unit: 'kg'
      }, {
        id: 'G006-W',
        name: 'Karotten gewürfelt (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231013-01-W',
        amount: 8,
        unit: 'kg'
      }, {
        id: 'G007-W',
        name: 'Sellerie gewürfelt (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231013-02-W',
        amount: 5,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'VM-003',
        name: 'Gemüsemischung Bolognese',
        type: 'Vormischung',
        batchNumber: 'VM-1003-1',
        amount: 23,
        unit: 'kg'
      }],
      batchNumber: 'M-1003-1',
      notes: 'Mischung gemäß Rezept. Mischzeit: 10 Minuten.'
    }, {
      id: 'PS1003-5',
      type: 'cooking',
      name: 'Anbraten des Fleisches',
      timestamp: new Date(2023, 9, 14, 10, 0),
      duration: 30,
      operator: 'A. Fischer',
      machine: 'Kochkessel-01',
      temperature: 160,
      inputMaterials: [{
        id: 'F002-W',
        name: 'Rinderhackfleisch (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'F-20231013-01-W',
        amount: 30,
        unit: 'kg'
      }, {
        id: 'OL001',
        name: 'Olivenöl',
        type: 'Öl',
        supplier: 'Öl GmbH',
        batchNumber: 'OL-20231001-05',
        amount: 2,
        unit: 'l'
      }],
      outputMaterials: [{
        id: 'FA-001',
        name: 'Rinderhackfleisch angebraten',
        type: 'Zwischenprodukt',
        batchNumber: 'FA-1003-1',
        amount: 28,
        unit: 'kg'
      }],
      batchNumber: 'C-1003-1',
      notes: 'Anbraten bei hoher Temperatur. Fett abgelassen. Temperaturkontrolle dokumentiert.'
    }, {
      id: 'PS1003-6',
      type: 'cooking',
      name: 'Anbraten des Gemüses',
      timestamp: new Date(2023, 9, 14, 10, 40),
      duration: 20,
      operator: 'A. Fischer',
      machine: 'Kochkessel-02',
      temperature: 140,
      inputMaterials: [{
        id: 'VM-003',
        name: 'Gemüsemischung Bolognese',
        type: 'Vormischung',
        batchNumber: 'VM-1003-1',
        amount: 23,
        unit: 'kg'
      }, {
        id: 'OL001',
        name: 'Olivenöl',
        type: 'Öl',
        supplier: 'Öl GmbH',
        batchNumber: 'OL-20231001-05',
        amount: 1,
        unit: 'l'
      }],
      outputMaterials: [{
        id: 'GA-001',
        name: 'Gemüsemischung angebraten',
        type: 'Zwischenprodukt',
        batchNumber: 'GA-1003-1',
        amount: 21,
        unit: 'kg'
      }],
      batchNumber: 'C-1003-2',
      notes: 'Anbraten bei mittlerer Temperatur. Temperaturkontrolle dokumentiert.'
    }, {
      id: 'PS1003-7',
      type: 'cooking',
      name: 'Hauptkochvorgang Bolognese',
      timestamp: new Date(2023, 9, 14, 11, 10),
      duration: 120,
      operator: 'A. Fischer',
      machine: 'Hauptkochkessel-01',
      temperature: 98,
      inputMaterials: [{
        id: 'FA-001',
        name: 'Rinderhackfleisch angebraten',
        type: 'Zwischenprodukt',
        batchNumber: 'FA-1003-1',
        amount: 28,
        unit: 'kg'
      }, {
        id: 'GA-001',
        name: 'Gemüsemischung angebraten',
        type: 'Zwischenprodukt',
        batchNumber: 'GA-1003-1',
        amount: 21,
        unit: 'kg'
      }, {
        id: 'G008-W',
        name: 'Tomaten passiert (verwogen)',
        type: 'Verwogene Zutat',
        batchNumber: 'G-20231010-03-W',
        amount: 25,
        unit: 'kg'
      }, {
        id: 'S-MIX-003',
        name: 'Bolognese Gewürzmischung',
        type: 'Gewürzmischung',
        batchNumber: 'SM-1003-1',
        amount: 0.37,
        unit: 'kg'
      }, {
        id: 'W001',
        name: 'Wasser',
        type: 'Flüssigkeit',
        amount: 10,
        unit: 'l'
      }],
      outputMaterials: [{
        id: 'BF-001',
        name: 'Bolognese fertig gekocht',
        type: 'Fertigprodukt',
        batchNumber: 'BF-1003-1',
        amount: 82,
        unit: 'kg'
      }],
      batchNumber: 'C-1003-3',
      notes: 'Kochvorgang gemäß Rezept. Köcheln bei 98°C für 110 Minuten. Regelmäßiges Umrühren. Temperaturkontrolle dokumentiert.'
    }, {
      id: 'PS1003-8',
      type: 'quality',
      name: 'Qualitätskontrolle',
      timestamp: new Date(2023, 9, 14, 13, 20),
      duration: 15,
      operator: 'D. Meyer',
      inputMaterials: [{
        id: 'BF-001',
        name: 'Bolognese fertig gekocht',
        type: 'Fertigprodukt',
        batchNumber: 'BF-1003-1',
        amount: 0.5,
        unit: 'kg'
      }],
      outputMaterials: [{
        id: 'BF-001-Q',
        name: 'Bolognese fertig gekocht (QS geprüft)',
        type: 'Fertigprodukt QS',
        batchNumber: 'BF-1003-1-Q',
        amount: 81.5,
        unit: 'kg'
      }],
      batchNumber: 'Q-1003-1',
      notes: 'Sensorische Prüfung bestanden. Mikrobiologische Proben entnommen. Freigabe erteilt.'
    }, {
      id: 'PS1003-9',
      type: 'packaging',
      name: 'Verpackung',
      timestamp: new Date(2023, 9, 14, 14, 0),
      duration: 60,
      operator: 'B. Wagner',
      machine: 'Abfüllanlage-01',
      inputMaterials: [{
        id: 'BF-001-Q',
        name: 'Bolognese fertig gekocht (QS geprüft)',
        type: 'Fertigprodukt QS',
        batchNumber: 'BF-1003-1-Q',
        amount: 81.5,
        unit: 'kg'
      }, {
        id: 'VP002',
        name: 'Verpackungsmaterial 500g',
        type: 'Verpackung',
        supplier: 'Verpackung GmbH',
        batchNumber: 'VP-20231001-02',
        amount: 165,
        unit: 'Stück'
      }],
      outputMaterials: [{
        id: 'BP-001',
        name: 'Gustoria Bolognese 500g',
        type: 'Endprodukt',
        batchNumber: 'B2023-B-1003',
        amount: 163,
        unit: 'Stück'
      }],
      batchNumber: 'P-1003-1',
      notes: 'Verpackung gemäß Spezifikation. Gewichtskontrolle durchgeführt. Etikettierung korrekt.'
    }, {
      id: 'PS1003-10',
      type: 'storage',
      name: 'Einlagerung TK-Lager',
      timestamp: new Date(2023, 9, 14, 15, 0),
      duration: 30,
      operator: 'K. Schulz',
      temperature: -18,
      inputMaterials: [{
        id: 'BP-001',
        name: 'Gustoria Bolognese 500g',
        type: 'Endprodukt',
        batchNumber: 'B2023-B-1003',
        amount: 163,
        unit: 'Stück'
      }],
      outputMaterials: [{
        id: 'BP-001-S',
        name: 'Gustoria Bolognese 500g (eingelagert)',
        type: 'Endprodukt gelagert',
        batchNumber: 'B2023-B-1003',
        amount: 163,
        unit: 'Stück'
      }],
      batchNumber: 'S-1003-1',
      notes: 'Einlagerung im TK-Lager bei -18°C. Lagerposition: TKL-A1-03. Haltbarkeit: 12 Monate.'
    }]
  }]);
  // Find process steps that match the search term
  const findMatchingSteps = (order: ProductionOrder, searchTerm: string) => {
    if (!searchTerm) return [];
    const normalizedSearch = searchTerm.toLowerCase();
    return order.processSteps.filter(step =>
    // Check step's own batch number
    step.batchNumber?.toLowerCase().includes(normalizedSearch) ||
    // Check input materials' batch numbers
    step.inputMaterials.some(material => material.batchNumber?.toLowerCase().includes(normalizedSearch)) ||
    // Check output materials' batch numbers
    step.outputMaterials?.some(material => material.batchNumber?.toLowerCase().includes(normalizedSearch))).map(step => step.id);
  };
  // Update matching steps when search term or selected order changes
  useEffect(() => {
    if (selectedOrder && searchTerm) {
      const matches = findMatchingSteps(selectedOrder, searchTerm);
      setMatchingStepIds(matches);
      // Auto-expand matching steps
      const newExpandedSteps = {
        ...expandedSteps
      };
      matches.forEach(stepId => {
        newExpandedSteps[stepId] = true;
      });
      setExpandedSteps(newExpandedSteps);
      // Scroll to first match after a short delay to ensure rendering
      if (matches.length > 0) {
        setTimeout(() => {
          const firstMatchRef = stepRefs.current[matches[0]];
          if (firstMatchRef) {
            firstMatchRef.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 300);
      }
    } else {
      setMatchingStepIds([]);
    }
  }, [selectedOrder, searchTerm]);
  // When selecting an order after search, preserve the search term
  const handleOrderSelect = (order: ProductionOrder) => {
    setSelectedOrder(order);
    // Matching steps will be set by the useEffect
  };
  // Filter orders based on search term and status
  const filteredOrders = productionOrders.filter(order => {
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesSearch = order.name.toLowerCase().includes(searchTerm.toLowerCase()) || order.id.includes(searchTerm) || order.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    // Search in process steps for batch numbers
    order.processSteps.some(step => step.batchNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    // Also search in input and output materials batch numbers
    step.inputMaterials.some(material => material.batchNumber?.toLowerCase().includes(searchTerm.toLowerCase())) || step.outputMaterials?.some(material => material.batchNumber?.toLowerCase().includes(searchTerm.toLowerCase())));
    return matchesStatus && matchesSearch;
  });
  // Toggle expanded state for process steps
  const toggleExpandStep = (stepId: string) => {
    setExpandedSteps(prev => ({
      ...prev,
      [stepId]: !prev[stepId]
    }));
  };
  // Function to format date and time
  const formatDateTime = (date: Date) => {
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Function to get step type icon
  const getStepTypeIcon = (type: string) => {
    switch (type) {
      case 'weighing':
        return <ScaleIcon size={18} className="text-blue-600" data-id="element-2364" />;
      case 'mixing':
        return <div size={18} className="text-purple-600" data-id="element-2365" />;
      case 'cooking':
        return <CookingPotIcon size={18} className="text-red-600" data-id="element-2366" />;
      case 'packaging':
        return <BoxIcon size={18} className="text-amber-600" data-id="element-2367" />;
      case 'quality':
        return <CheckCircleIcon size={18} className="text-green-600" data-id="element-2368" />;
      case 'storage':
        return <PackageIcon size={18} className="text-gray-600" data-id="element-2369" />;
      default:
        return <InfoIcon size={18} className="text-gray-600" data-id="element-2370" />;
    }
  };
  // Function to get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in-progress':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2371">
            <ClockIcon size={12} className="mr-1" data-id="element-2372" />
            In Bearbeitung
          </span>;
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2373">
            <CheckCircleIcon size={12} className="mr-1" data-id="element-2374" />
            Abgeschlossen
          </span>;
      case 'on-hold':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-2375">
            <ClockIcon size={12} className="mr-1" data-id="element-2376" />
            Pausiert
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2377">
            <InfoIcon size={12} className="mr-1" data-id="element-2378" />
            Unbekannt
          </span>;
    }
  };
  // Function to check if a process step matches the search term
  const isMatchingStep = (stepId: string) => {
    return matchingStepIds.includes(stepId);
  };
  // Function to render the material flow chart
  const renderMaterialFlowChart = () => {
    if (!selectedOrder) return null;
    return <div className="bg-white rounded-xl shadow-md p-6 mt-6" data-id="element-2379">
        <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-2380">
          Materialfluss-Diagramm
        </h3>
        <div className="overflow-x-auto" data-id="element-2381">
          <div className="min-w-[800px] p-4" data-id="element-2382">
            {selectedOrder.processSteps.map((step, index) => <div key={step.id} className={`mb-8 ${isMatchingStep(step.id) ? 'bg-blue-50 p-4 border border-blue-300 rounded-lg shadow-sm' : ''}`} ref={el => stepRefs.current[step.id] = el} data-id="element-2383">
                <div className="flex items-center mb-2" data-id="element-2384">
                  <div className="bg-gray-100 p-2 rounded-full mr-2" data-id="element-2385">
                    {getStepTypeIcon(step.type)}
                  </div>
                  <div className="font-medium text-gray-900" data-id="element-2386">
                    {step.name}
                    {isMatchingStep(step.id) && <span className="ml-2 text-xs font-normal text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full" data-id="element-2387">
                        Treffer: {step.batchNumber}
                      </span>}
                  </div>
                </div>
                <div className="ml-10 flex flex-col" data-id="element-2388">
                  <div className="flex flex-wrap gap-4 mb-2" data-id="element-2389">
                    {step.inputMaterials.map(material => <div key={material.id} className={`bg-blue-50 border border-blue-200 rounded-lg p-2 text-sm ${searchTerm && material.batchNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ? 'ring-2 ring-blue-400' : ''}`} data-id="element-2390">
                        <div className="font-medium text-blue-800" data-id="element-2391">
                          {material.name}
                        </div>
                        <div className="text-blue-600" data-id="element-2392">
                          {material.amount} {material.unit}
                        </div>
                        {material.batchNumber && <div className={`text-xs ${searchTerm && material.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()) ? 'bg-blue-100 text-blue-800 font-medium px-1 rounded' : 'text-blue-500'}`} data-id="element-2393">
                            Batch: {material.batchNumber}
                          </div>}
                      </div>)}
                  </div>
                  <div className="flex justify-center my-2" data-id="element-2394">
                    <div className="bg-gray-200 p-2 rounded-lg" data-id="element-2395">
                      <ArrowDownIcon className="text-gray-500" data-id="element-2396" />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 mt-2" data-id="element-2397">
                    {step.outputMaterials?.map(material => <div key={material.id} className={`bg-green-50 border border-green-200 rounded-lg p-2 text-sm ${searchTerm && material.batchNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ? 'ring-2 ring-blue-400' : ''}`} data-id="element-2398">
                        <div className="font-medium text-green-800" data-id="element-2399">
                          {material.name}
                        </div>
                        <div className="text-green-600" data-id="element-2400">
                          {material.amount} {material.unit}
                        </div>
                        {material.batchNumber && <div className={`text-xs ${searchTerm && material.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()) ? 'bg-blue-100 text-blue-800 font-medium px-1 rounded' : 'text-green-500'}`} data-id="element-2401">
                            Batch: {material.batchNumber}
                          </div>}
                      </div>)}
                  </div>
                </div>
                {index < selectedOrder.processSteps.length - 1 && <div className="flex justify-center my-4" data-id="element-2402">
                    <div className="h-8 border-l-2 border-dashed border-gray-300" data-id="element-2403"></div>
                  </div>}
              </div>)}
          </div>
        </div>
      </div>;
  };
  // Create a reusable search component
  const SearchComponent = () => <div className="relative flex-grow" data-id="element-2404">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2405">
        <SearchIcon size={18} className="text-gray-400" data-id="element-2406" />
      </div>
      <input type="text" className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md" placeholder="Nach Auftrag oder Chargennummer suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2407" />
    </div>;
  return <div className="container mx-auto" data-id="element-2408">
      {/* Header */}
      <div className="mb-8" data-id="element-2409">
        <div className="flex justify-between items-start" data-id="element-2410">
          <div data-id="element-2411">
            <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-2412">
              Rückverfolgung
            </h2>
            <p className="text-gray-600" data-id="element-2413">
              Rückverfolgung von Rohstoffen und Produktionsprozessen
            </p>
          </div>
          {onBackToOverview}
        </div>
      </div>
      {/* Order selection or order detail view */}
      {!selectedOrder ? <div className="bg-white rounded-xl shadow-md p-6" data-id="element-2414">
          <div className="mb-6" data-id="element-2415">
            <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-2416">
              Produktionsaufträge
            </h3>
            <p className="text-gray-600 mb-4" data-id="element-2417">
              Wählen Sie einen Auftrag aus, um die vollständige
              Rückverfolgbarkeit anzuzeigen
            </p>
            {/* Search and filter bar */}
            <div className="flex flex-col md:flex-row gap-4 mb-6" data-id="element-2418">
              <SearchComponent data-id="element-2419" />
              <div className="flex items-center" data-id="element-2420">
                <FilterIcon size={18} className="text-gray-500 mr-2" data-id="element-2421" />
                <select className="p-2 border border-gray-300 rounded-md" value={statusFilter} onChange={e => setStatusFilter(e.target.value as 'all' | 'in-progress' | 'completed' | 'on-hold')} data-id="element-2422">
                  <option value="all" data-id="element-2423">Alle Aufträge</option>
                  <option value="in-progress" data-id="element-2424">In Bearbeitung</option>
                  <option value="completed" data-id="element-2425">Abgeschlossen</option>
                  <option value="on-hold" data-id="element-2426">Pausiert</option>
                </select>
              </div>
            </div>
            {/* Orders list */}
            <div className="space-y-4" data-id="element-2427">
              {filteredOrders.map(order => <div key={order.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleOrderSelect(order)} data-id="element-2428">
                  <div className="flex justify-between items-start" data-id="element-2429">
                    <div data-id="element-2430">
                      <h4 className="font-medium text-gray-900" data-id="element-2431">
                        {order.name}
                      </h4>
                      <p className="text-sm text-gray-500" data-id="element-2432">
                        Auftrags-Nr: #{order.id}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-2433">
                        Chargennummer: {order.batchNumber}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-2434">
                        Start: {formatDateTime(order.startDate)}
                      </p>
                      {order.endDate && <p className="text-sm text-gray-500" data-id="element-2435">
                          Ende: {formatDateTime(order.endDate)}
                        </p>}
                    </div>
                    <div className="flex flex-col items-end" data-id="element-2436">
                      {getStatusBadge(order.status)}
                      <div className="mt-3 text-sm text-gray-500" data-id="element-2437">
                        {order.processSteps.length} Prozessschritte
                      </div>
                    </div>
                  </div>
                </div>)}
              {filteredOrders.length === 0 && <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200" data-id="element-2438">
                  <SearchIcon size={24} className="mx-auto text-gray-400 mb-2" data-id="element-2439" />
                  <p className="text-gray-600" data-id="element-2440">
                    Keine Aufträge gefunden, die den Suchkriterien entsprechen.
                  </p>
                </div>}
            </div>
          </div>
        </div> : <div data-id="element-2441">
          {/* Order detail view */}
          <button onClick={() => setSelectedOrder(null)} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4" data-id="element-2442">
            <ArrowLeftIcon size={16} data-id="element-2443" />
            <span data-id="element-2444">Zurück zur Auftragsübersicht</span>
          </button>
          <div className="bg-white rounded-xl shadow-md p-6" data-id="element-2445">
            <div className="mb-6" data-id="element-2446">
              <div className="flex flex-col md:flex-row justify-between items-start gap-4" data-id="element-2447">
                <div data-id="element-2448">
                  <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-2449">
                    {selectedOrder.name}
                  </h3>
                  <p className="text-gray-600" data-id="element-2450">
                    Auftrags-Nr: #{selectedOrder.id} | Chargennummer:{' '}
                    {selectedOrder.batchNumber}
                  </p>
                  <p className="text-gray-600" data-id="element-2451">
                    Start: {formatDateTime(selectedOrder.startDate)}
                    {selectedOrder.endDate && ` | Ende: ${formatDateTime(selectedOrder.endDate)}`}
                  </p>
                </div>
                <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto" data-id="element-2452">
                  {/* Search bar in detail view */}
                  <SearchComponent data-id="element-2453" />
                  <div data-id="element-2454">{getStatusBadge(selectedOrder.status)}</div>
                </div>
              </div>
              {/* Show search result summary if there are matches */}
              {matchingStepIds.length > 0 && <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center" data-id="element-2455">
                  <InfoIcon size={18} className="text-blue-600 mr-2" data-id="element-2456" />
                  <span className="text-blue-800" data-id="element-2457">
                    <strong data-id="element-2458">{matchingStepIds.length}</strong> Prozessschritte
                    gefunden, die "{searchTerm}" enthalten.
                  </span>
                </div>}
              {/* Show "no matches" message if searching but no matches */}
              {searchTerm && matchingStepIds.length === 0 && <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center" data-id="element-2459">
                  <InfoIcon size={18} className="text-gray-500 mr-2" data-id="element-2460" />
                  <span className="text-gray-600" data-id="element-2461">
                    Keine Prozessschritte gefunden, die "{searchTerm}"
                    enthalten.
                  </span>
                </div>}
            </div>
            {/* Tabs for different views */}
            <div className="border-b border-gray-200 mb-6" data-id="element-2462">
              <div className="flex space-x-4" data-id="element-2463">
                <button className={`pb-2 px-1 ${activeTab === 'timeline' ? 'border-b-2 border-[#8DC63F] text-[#8DC63F] font-medium' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('timeline')} data-id="element-2464">
                  Zeitleiste
                </button>
                <button className={`pb-2 px-1 ${activeTab === 'flowchart' ? 'border-b-2 border-[#8DC63F] text-[#8DC63F] font-medium' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('flowchart')} data-id="element-2465">
                  Materialfluss
                </button>
              </div>
            </div>
            {/* Timeline view */}
            {activeTab === 'timeline' && <div className="space-y-4" data-id="element-2466">
                <h4 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-2467">
                  Prozessschritte
                </h4>
                <div className="space-y-4" data-id="element-2468">
                  {selectedOrder.processSteps.map(step => <div key={step.id} className={`border rounded-lg overflow-hidden ${isMatchingStep(step.id) ? 'border-blue-300 bg-blue-50 shadow-sm' : ''}`} ref={el => stepRefs.current[step.id] = el} data-id="element-2469">
                      <div className={`${isMatchingStep(step.id) ? 'bg-blue-100' : 'bg-gray-50'} p-4 flex justify-between items-center cursor-pointer`} onClick={() => toggleExpandStep(step.id)} data-id="element-2470">
                        <div className="flex items-center" data-id="element-2471">
                          <div className="mr-3" data-id="element-2472">
                            {getStepTypeIcon(step.type)}
                          </div>
                          <div data-id="element-2473">
                            <h5 className="font-medium text-gray-900 flex items-center" data-id="element-2474">
                              {step.name}
                              {isMatchingStep(step.id) && <span className="ml-2 text-xs font-normal text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full" data-id="element-2475">
                                  Treffer: {step.batchNumber}
                                </span>}
                            </h5>
                            <p className="text-sm text-gray-500" data-id="element-2476">
                              {formatDateTime(step.timestamp)} |{' '}
                              {step.duration ? `Dauer: ${step.duration} Minuten` : 'Keine Dauerangabe'}
                            </p>
                          </div>
                        </div>
                        <div data-id="element-2477">
                          {expandedSteps[step.id] ? <ChevronDownIcon size={20} className="text-gray-500" data-id="element-2478" /> : <ChevronRightIcon size={20} className="text-gray-500" data-id="element-2479" />}
                        </div>
                      </div>
                      {expandedSteps[step.id] && <div className="p-4 border-t" data-id="element-2480">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-2481">
                            <div data-id="element-2482">
                              <h6 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2483">
                                Details
                              </h6>
                              <div className="bg-gray-50 rounded-lg p-3 text-sm" data-id="element-2484">
                                <p className="mb-1" data-id="element-2485">
                                  <span className="font-medium" data-id="element-2486">
                                    Batchnummer:
                                  </span>{' '}
                                  <span className={searchTerm && step.batchNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ? 'bg-blue-100 text-blue-800 font-medium px-1 rounded' : ''} data-id="element-2487">
                                    {step.batchNumber || 'Keine Angabe'}
                                  </span>
                                </p>
                                {step.operator && <p className="mb-1" data-id="element-2488">
                                    <span className="font-medium" data-id="element-2489">
                                      Operator:
                                    </span>{' '}
                                    {step.operator}
                                  </p>}
                                {step.machine && <p className="mb-1" data-id="element-2490">
                                    <span className="font-medium" data-id="element-2491">
                                      Maschine:
                                    </span>{' '}
                                    {step.machine}
                                  </p>}
                                {step.temperature && <p className="mb-1" data-id="element-2492">
                                    <span className="font-medium" data-id="element-2493">
                                      Temperatur:
                                    </span>{' '}
                                    {step.temperature}°C
                                  </p>}
                                {step.notes && <p className="mb-1" data-id="element-2494">
                                    <span className="font-medium" data-id="element-2495">
                                      Anmerkungen:
                                    </span>{' '}
                                    {step.notes}
                                  </p>}
                              </div>
                            </div>
                            <div data-id="element-2496">
                              <h6 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2497">
                                Materialfluss
                              </h6>
                              <div className="bg-gray-50 rounded-lg p-3 text-sm" data-id="element-2498">
                                <div className="mb-2" data-id="element-2499">
                                  <p className="font-medium text-gray-700" data-id="element-2500">
                                    Eingangsmaterialien:
                                  </p>
                                  <ul className="list-disc pl-5 space-y-1" data-id="element-2501">
                                    {step.inputMaterials.map(material => <li key={material.id} data-id="element-2502">
                                        {material.name} ({material.amount}{' '}
                                        {material.unit})
                                        {material.batchNumber && <span className={searchTerm && material.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()) ? 'bg-blue-100 text-blue-800 font-medium ml-1 px-1 rounded' : 'text-gray-500'} data-id="element-2503">
                                            {' '}
                                            | Batch: {material.batchNumber}
                                          </span>}
                                      </li>)}
                                  </ul>
                                </div>
                                {step.outputMaterials && <div data-id="element-2504">
                                    <p className="font-medium text-gray-700" data-id="element-2505">
                                      Ausgangsmaterialien:
                                    </p>
                                    <ul className="list-disc pl-5 space-y-1" data-id="element-2506">
                                      {step.outputMaterials.map(material => <li key={material.id} data-id="element-2507">
                                          {material.name} ({material.amount}{' '}
                                          {material.unit})
                                          {material.batchNumber && <span className={searchTerm && material.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()) ? 'bg-blue-100 text-blue-800 font-medium ml-1 px-1 rounded' : 'text-gray-500'} data-id="element-2508">
                                              {' '}
                                              | Batch: {material.batchNumber}
                                            </span>}
                                        </li>)}
                                    </ul>
                                  </div>}
                              </div>
                            </div>
                          </div>
                        </div>}
                    </div>)}
                </div>
              </div>}
            {/* Flowchart view */}
            {activeTab === 'flowchart' && renderMaterialFlowChart()}
          </div>
        </div>}
    </div>;
}